import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenisManager";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled || open
            ? "bg-cream/90 backdrop-blur-md border-b border-line"
            : "bg-transparent"
        }`}
      >
        <div className="container-editorial flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => go("home")}
            className="font-display text-2xl md:text-[26px] leading-none tracking-tight text-ink"
            aria-label="Sona home"
          >
            SONA
            <span className="ml-2 align-middle kicker hidden sm:inline">FOOD · TRAVEL · LIFESTYLE</span>
          </button>

          <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => go(n.id)}
                className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/80 hover:text-ink transition-colors"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-cta-whatsapp"
              href={LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex btn-primary !py-3 !px-6"
            >
              Work With Sona
            </a>
            <button
              data-testid="nav-hamburger"
              onClick={() => setOpen((o) => !o)}
              className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-ink/25 text-ink"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 md:hidden bg-cream"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="grain h-full flex flex-col justify-center container-editorial">
              <nav className="flex flex-col gap-2" aria-label="Mobile">
                {NAV.map((n, i) => (
                  <motion.button
                    key={n.id}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={() => go(n.id)}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                    className="text-left font-display text-5xl leading-tight text-ink py-2 border-b border-line/70"
                  >
                    <span className="kicker mr-4 align-middle">{`0${i + 1}`}</span>
                    {n.label}
                  </motion.button>
                ))}
              </nav>
              <a
                data-testid="mobile-cta-whatsapp"
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-10 w-full"
              >
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
